import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  LinearProgress,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { subscribeToStreamListChanges, subscribeToPlatformListChanges } from "../context/AppContext";
import ConnectingAirportsIcon from "@mui/icons-material/ConnectingAirports";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import TableRowsIcon from "@mui/icons-material/TableRows";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { useStreamsState } from "../context/StreamsStateContext";
import PlatformDialog from "../components/PlatformDialog";
import { PlatformSummary } from "../types/platform";
import type { StreamSummary } from "../types/streamSummary";
import PlatformCards from "../components/PlatformCards";
import PlatformTable from "../components/PlatformTable";
import { normalizeTags } from "../utils/tags";

type DeleteStreamsAction = "delete" | "detach";
type ExportPlatformPayload = Omit<PlatformSummary, "id">;
type ExportStreamPayload = Omit<StreamSummary, "id" | "platformId" | "platformName" | "state">;
type ExportedPlatformConfig = {
  platform: ExportPlatformPayload;
  streams: ExportStreamPayload[];
};
type ExportedConfig = {
  platforms: ExportedPlatformConfig[];
};

const FALLBACK_PLATFORM_COLOR = "#2563eb";
type PlatformsViewMode = "Cards" | "Table";
const PLATFORMS_VIEW_MODE_STORAGE_KEY = "platformsPageViewMode";

type StreamDeleteProgressPayload = {
  jobId: string;
  total: number;
  completed: number;
  failed: number;
  status: "running" | "completed" | "failed";
  currentId?: string;
  currentError?: string;
};

const PlatformsPage = () => {
  const { authorizedFetch, user, userManagementEnabled, tokens, resolveApiPath } = useAuth();
  const canManagePlatforms = !userManagementEnabled || user?.role === "admin";
  const queryClient = useQueryClient();
  const { getStreamState } = useStreamsState();
  const theme = useTheme();
  const isNarrowToolbar = useMediaQuery(theme.breakpoints.down("md"));
  const [platformsViewMode, setPlatformsViewMode] = useState<PlatformsViewMode>(() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(PLATFORMS_VIEW_MODE_STORAGE_KEY);
      if (stored === "Cards" || stored === "Table") {
        return stored;
      }
    }
    return "Table";
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(PLATFORMS_VIEW_MODE_STORAGE_KEY, platformsViewMode);
    } catch (err) {
      console.error("Failed to persist platforms view mode", err);
    }
  }, [platformsViewMode]);

  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery<PlatformSummary[]>({
    queryKey: ["platforms"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/platforms");
      const payload = (await response.json().catch(() => null)) as { platforms?: PlatformSummary[]; error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load platforms");
      }
      return payload?.platforms ?? [];
    }
  });

  const platforms = useMemo(() => data ?? [], [data]);

  const {
    data: streamsData,
    isLoading: isStreamsLoading,
    isError: isStreamsError,
    error: streamsError
  } = useQuery<StreamSummary[]>({
    queryKey: ["streams"],
    queryFn: async () => {
      const response = await authorizedFetch("/api/streams");
      const payload = (await response.json().catch(() => null)) as { streams?: StreamSummary[]; error?: string } | null;
      if (!response.ok) {
        throw new Error(payload?.error ?? "Unable to load streams");
      }
      return payload?.streams ?? [];
    }
  });

  const streams = useMemo(() => streamsData ?? [], [streamsData]);

  const streamsByPlatform = useMemo(() => {
    const map = new Map<string, StreamSummary[]>();
    for (const stream of streams) {
      const platformId = stream.platformId?.trim();
      if (!platformId) {
        continue;
      }
      if (!map.has(platformId)) {
        map.set(platformId, []);
      }
      map.get(platformId)!.push(stream);
    }
    for (const entries of map.values()) {
      entries.sort((a, b) => a.sensor.localeCompare(b.sensor));
    }
    return map;
  }, [streams]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState<PlatformSummary | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteAction, setDeleteAction] = useState<DeleteStreamsAction>("detach");
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<StreamDeleteProgressPayload | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [selectedPlatformId, setSelectedPlatformId] = useState<string | null>(null);
  const [selectedPlatformIds, setSelectedPlatformIds] = useState<string[]>([]);
  const [deleteTargets, setDeleteTargets] = useState<PlatformSummary[]>([]);
  const bulkDeleteSourceRef = useRef<EventSource | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const selectedPlatform = useMemo(
    () => platforms.find((platform) => platform.id === selectedPlatformId) ?? null,
    [platforms, selectedPlatformId]
  );

  const invalidatePlatforms = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["platforms"] });
  }, [queryClient]);

  const invalidateStreams = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["streams"] });
  }, [queryClient]);

  // Subscribe to stream list changes from WebSocket
  useEffect(() => {
    const unsubscribe = subscribeToStreamListChanges((message) => {
      console.log("[PlatformsPage] Stream list changed:", message.data);
      invalidateStreams();
    });
    return unsubscribe;
  }, [invalidateStreams]);

  // Subscribe to platform list changes from WebSocket
  useEffect(() => {
    const unsubscribe = subscribeToPlatformListChanges((message) => {
      console.log("[PlatformsPage] Platform list changed:", message.data);
      invalidatePlatforms();
    });
    return unsubscribe;
  }, [invalidatePlatforms]);

  useEffect(() => {
    if (platformsViewMode !== "Table") {
      setSelectedPlatformId(null);
      setSelectedPlatformIds([]);
    }
  }, [platformsViewMode]);

  useEffect(() => {
    setSelectedPlatformIds((current) => {
      const validIds = current.filter((id) => platforms.some((platform) => platform.id === id));
      return validIds.length === current.length ? current : validIds;
    });
    if (selectedPlatformId && !platforms.some((platform) => platform.id === selectedPlatformId)) {
      setSelectedPlatformId(null);
    }
  }, [platforms, selectedPlatformId]);

  const handleSelectionChange = useCallback((primaryId: string | null, ids: string[]) => {
    setSelectedPlatformId(primaryId);
    setSelectedPlatformIds(ids);
  }, []);

  const deleteMutation = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: DeleteStreamsAction }) => {
      const response = await authorizedFetch(`/api/platforms/${id}?streamAction=${action}`, {
        method: "DELETE"
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = payload?.error ?? "Unable to delete platform";
        throw new Error(message);
      }
    },
    onSuccess: () => {
      invalidatePlatforms();
      invalidateStreams();
    }
  });

  const closeBulkDeleteStream = useCallback(() => {
    if (bulkDeleteSourceRef.current) {
      bulkDeleteSourceRef.current.close();
      bulkDeleteSourceRef.current = null;
    }
  }, []);

  const resetBulkDeleteState = useCallback(() => {
    closeBulkDeleteStream();
    setBulkDeleteProgress(null);
  }, [closeBulkDeleteStream]);

  useEffect(() => () => closeBulkDeleteStream(), [closeBulkDeleteStream]);

  const handleOpenCreate = () => {
    if (!canManagePlatforms) {
      return;
    }
    setEditingPlatform(null);
    setDialogOpen(true);
  };

  const handleViewModeChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, value: PlatformsViewMode | null) => {
      if (!value) {
        return;
      }
      setPlatformsViewMode(value);
    },
    []
  );

  const handleOpenEdit = (platform: PlatformSummary) => {
    if (!canManagePlatforms) {
      return;
    }
    setEditingPlatform(platform);
    setDialogOpen(true);
  };

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingPlatform(null);
  }, []);

  const handleDialogSuccess = useCallback(
    ({ name, isEdit }: { name: string; isEdit: boolean }) => {
      invalidatePlatforms();
      setSnackbar({
        open: true,
        message: isEdit ? `${name} updated.` : `${name} added.`,
        severity: "success"
      });
      handleCloseDialog();
    },
    [handleCloseDialog, invalidatePlatforms]
  );

  const handleOpenDelete = (platform: PlatformSummary, queue: PlatformSummary[] = []) => {
    if (!canManagePlatforms) {
      return;
    }
    resetBulkDeleteState();
    setDeleteTargets([platform, ...queue]);
    setDeleteError(null);
    setDeleteAction("detach");
  };

  const handleCloseDelete = () => {
    if (bulkDeleteProgress?.status === "running") {
      return;
    }
    setDeleteTargets([]);
    setDeleteError(null);
    setDeleteAction("detach");
    resetBulkDeleteState();
  };

  const startBulkStreamDelete = useCallback(
    async (streamIds: string[]) => {
      const response = await authorizedFetch("/api/streams/bulk-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: streamIds })
      });
      const payload = (await response.json().catch(() => null)) as { jobId?: string; total?: number; error?: string } | null;
      if (!response.ok || !payload?.jobId) {
        const message = payload?.error ?? "Unable to delete streams.";
        throw new Error(message);
      }

      setBulkDeleteProgress({
        jobId: payload.jobId,
        total: payload.total ?? streamIds.length,
        completed: 0,
        failed: 0,
        status: "running"
      });

      return await new Promise<StreamDeleteProgressPayload>((resolve, reject) => {
        const sseUrl = new URL(resolveApiPath(`/api/streams/bulk-delete/${payload.jobId}/events`), window.location.href);
        if (userManagementEnabled && tokens?.accessToken) {
          sseUrl.searchParams.set("access_token", tokens.accessToken);
        }
        const source = new EventSource(sseUrl.toString());
        bulkDeleteSourceRef.current = source;

        source.onmessage = (event) => {
          try {
            const update = JSON.parse(event.data) as StreamDeleteProgressPayload;
            setBulkDeleteProgress(update);
            if (update.status !== "running") {
              closeBulkDeleteStream();
              resolve(update);
            }
          } catch (err) {
            console.error("Failed to parse delete progress", err);
          }
        };

        source.onerror = (event) => {
          console.error("Bulk delete progress error", event);
          closeBulkDeleteStream();
          setBulkDeleteProgress((current) =>
            current ? { ...current, status: "failed" } : current
          );
          reject(new Error("Bulk delete progress stream interrupted."));
        };
      });
    },
    [authorizedFetch, closeBulkDeleteStream, resolveApiPath, tokens?.accessToken, userManagementEnabled]
  );

  const handleConfirmDelete = async () => {
    if (!canManagePlatforms || deleteTargets.length === 0) return;
    setDeleteError(null);
    try {
      let platformDeleteAction: DeleteStreamsAction = deleteAction;
      if (deleteAction === "delete" && deletePlatformStreamCount > 0) {
        const streamIds = new Set<string>();
        deleteTargets.forEach((platform) => {
          const entries = streamsByPlatform.get(platform.id);
          if (entries) {
            entries.forEach((stream) => streamIds.add(stream.id));
          }
        });
        const result = await startBulkStreamDelete(Array.from(streamIds));
        if (result.failed > 0) {
          setDeleteError(`${result.failed} of ${result.total} stream deletions failed.`);
          return;
        }
        invalidateStreams();
        platformDeleteAction = "detach";
      }

      if (deleteAction === "detach" && streamsByPlatform.size > 0) {
        const platformIds = new Set(deleteTargets.map((platform) => platform.id));
        const streamsToDetach: StreamSummary[] = [];
        platformIds.forEach((id) => {
          const entries = streamsByPlatform.get(id);
          if (entries) {
            streamsToDetach.push(...entries);
          }
        });

        const uniqueStreams = new Map<string, StreamSummary>();
        streamsToDetach.forEach((stream) => {
          if (!uniqueStreams.has(stream.id)) {
            uniqueStreams.set(stream.id, stream);
          }
        });

        const detachErrors: string[] = [];
        for (const stream of uniqueStreams.values()) {
          const payload = {
            sensor: stream.sensor,
            platformId: "",
            type: stream.type,
            url: stream.url,
            networkInterface: stream.networkInterface ?? "",
            description: stream.description ?? "",
            active: false,
            autoStart: stream.autoStart,
            passthrough: Boolean(stream.passthrough),
            live: stream.live ?? null,
            srt: stream.srt ?? null,
            timeout: stream.timeout ?? null,
            offlineAfter: stream.offlineAfter ?? null,
            bufferSize: stream.bufferSize ?? null,
            addPlatformMetadata: false
          };
          try {
            const response = await authorizedFetch(`/api/streams/${stream.id}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload)
            });
            if (!response.ok) {
              const data = await response.json().catch(() => null);
              const message = data?.error ?? "Unable to detach stream";
              detachErrors.push(`${stream.sensor}: ${message}`);
            }
          } catch (err) {
            const message = err instanceof Error ? err.message : "Unable to detach stream";
            detachErrors.push(`${stream.sensor}: ${message}`);
          }
        }

        if (detachErrors.length > 0) {
          setDeleteError(detachErrors.join(" "));
          return;
        }
      }

      const remaining: PlatformSummary[] = [];
      let deletedCount = 0;
      const errors: string[] = [];

      for (const platform of deleteTargets) {
        try {
          await deleteMutation.mutateAsync({ id: platform.id, action: platformDeleteAction });
          deletedCount += 1;
        } catch (err) {
          console.error(err);
          const message = err instanceof Error ? err.message : "Unable to delete platform.";
          errors.push(`${platform.name}: ${message}`);
          remaining.push(platform);
        }
      }

      const actionMessage =
        deleteAction === "delete"
          ? "Associated streams deleted."
          : "Associated streams retained.";
      if (deletedCount > 0) {
        setSnackbar({
          open: true,
          message:
            deletedCount === 1
              ? `${deleteTargets[0].name} removed. ${actionMessage}`
              : `${deletedCount} platforms removed. ${actionMessage}`,
          severity: "success"
        });
      }
      if (errors.length > 0) {
        setSnackbar({
          open: true,
          message: `Some platforms were not removed: ${errors.join(" ")}`,
          severity: "error"
        });
      }
      setSelectedPlatformId(null);
      setSelectedPlatformIds([]);
      setDeleteTargets([]);
      setDeleteAction("detach");
      resetBulkDeleteState();
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : "Unable to delete platform.";
      setDeleteError(message);
      resetBulkDeleteState();
    }
  };

  const errorMessage = isError ? (error instanceof Error ? error.message : "Unable to load platforms") : null;
  const streamsErrorMessage = isStreamsError
    ? (streamsError instanceof Error ? streamsError.message : "Unable to load streams")
    : null;
  const deletePlatformStreamCount = deleteTargets.reduce(
    (total, platform) => total + (streamsByPlatform.get(platform.id)?.length ?? 0),
    0
  );
  const isBulkDeleteRunning = bulkDeleteProgress?.status === "running";
  const bulkDeleteProgressPercent =
    bulkDeleteProgress && bulkDeleteProgress.total > 0
      ? Math.round((bulkDeleteProgress.completed / bulkDeleteProgress.total) * 100)
      : 0;
  const shouldShowStreamOptions = !isStreamsLoading && !streamsErrorMessage && deletePlatformStreamCount > 0;
  const showNoStreamMessage = !isStreamsLoading && !streamsErrorMessage && deleteTargets.length > 0 && deletePlatformStreamCount === 0;
  const activeDeletePlatform = deleteTargets[0] ?? null;
  const selectionCount = selectedPlatformIds.length > 0 ? selectedPlatformIds.length : selectedPlatformId ? 1 : 0;
  const hasSingleSelection = selectionCount === 1;
  const hasAnySelection = selectionCount > 0;

  const triggerSnackbar = useCallback(
    (message: string, severity: "success" | "error") => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const handleExport = useCallback(() => {
    if (!canManagePlatforms) {
      return;
    }
    const ids = selectedPlatformIds.length > 0 ? selectedPlatformIds : selectedPlatformId ? [selectedPlatformId] : [];
    const selectedIds = Array.from(new Set(ids));
    if (selectedIds.length === 0) {
      return;
    }

    const selectedPlatforms = platforms.filter((platform) => selectedIds.includes(platform.id));
    if (selectedPlatforms.length === 0) {
      return;
    }

    setIsExporting(true);
    try {
      const sanitizePlatform = (platform: PlatformSummary) => {
        const geoInfo = platform.geoInfo;
        const hasGeo =
          geoInfo &&
          (geoInfo.latitude !== undefined ||
            geoInfo.longitude !== undefined ||
            geoInfo.altitude !== undefined ||
            geoInfo.heading !== undefined);

        return {
          name: platform.name.trim(),
          type: platform.type.trim(),
          model: platform.model?.trim() ?? "",
          description: platform.description?.trim() ?? "",
          color: platform.color?.trim() || FALLBACK_PLATFORM_COLOR,
          tags: normalizeTags(platform.tags ?? []),
          geoInfo: hasGeo ? geoInfo : undefined
        };
      };

      const sanitizeStreamTargets = (targets?: StreamSummary["targets"]) => {
        if (!targets) {
          return targets;
        }
        return targets.map((target) => {
          const { id: _targetId, ...targetRest } = target;
          return targetRest;
        });
      };

      const sanitizeStream = (stream: StreamSummary) => {
        const {
          id: _streamId,
          platformId: _ignoredPlatformId,
          platformName: _platformName,
          state: _state,
          targets,
          ...rest
        } = stream;
        return {
          ...rest,
          sensor: stream.sensor.trim(),
          type: stream.type.trim(),
          url: stream.url.trim(),
          networkInterface: stream.networkInterface?.trim() ?? "",
          description: stream.description?.trim() ?? "",
          targets: sanitizeStreamTargets(targets) as any
        };
      };

      const exportPayload: ExportedConfig = {
        platforms: selectedPlatforms.map((platform) => ({
          platform: sanitizePlatform(platform),
          streams: (streamsByPlatform.get(platform.id) ?? []).map(sanitizeStream)
        }))
      };

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = url;
      link.download = `platforms-config-${timestamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      triggerSnackbar("Configuration exported.", "success");
    } catch (err) {
      console.error("Export failed", err);
      triggerSnackbar("Failed to export configuration.", "error");
    } finally {
      setIsExporting(false);
    }
  }, [
    canManagePlatforms,
    platforms,
    selectedPlatformId,
    selectedPlatformIds,
    streamsByPlatform,
    triggerSnackbar
  ]);

  const getSelectedPlatformIds = useCallback(() => {
    const ids = selectedPlatformIds.length > 0 ? selectedPlatformIds : selectedPlatformId ? [selectedPlatformId] : [];
    return Array.from(new Set(ids));
  }, [selectedPlatformId, selectedPlatformIds]);

  const handleDuplicate = useCallback(async () => {
    if (!canManagePlatforms) {
      return;
    }
    const targets = getSelectedPlatformIds();
    if (targets.length === 0) {
      return;
    }
    const platformsToCopy = platforms.filter((platform) => targets.includes(platform.id));
    if (platformsToCopy.length === 0) {
      return;
    }
    const createdIds: string[] = [];
    setIsDuplicating(true);
    try {
      for (const platform of platformsToCopy) {
        const copyName = `${platform.name.trim()}-Copy`;
        const color = (() => {
          const trimmed = platform.color?.trim() || FALLBACK_PLATFORM_COLOR;
          return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
        })();
        const payload = {
          name: copyName,
          type: platform.type.trim(),
          model: platform.model?.trim() ?? "",
          description: platform.description?.trim() ?? "",
          color,
          tags: normalizeTags(platform.tags ?? []),
          geoInfo: platform.geoInfo
        };
        const response = await authorizedFetch("/api/platforms", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          const message = data?.error ?? `Unable to duplicate platform ${platform.name}`;
          throw new Error(message);
        }

        const data = await response.json().catch(() => null);
        if (data && typeof data === "object") {
          if ("platform" in data && data.platform && typeof data.platform === "object") {
            const platformRecord = data.platform as PlatformSummary;
            if (platformRecord.id) {
              createdIds.push(platformRecord.id);
            }
          } else if ("id" in data && typeof (data as Record<string, unknown>).id === "string") {
            createdIds.push((data as Record<string, unknown>).id as string);
          }
        }
      }

      setSelectedPlatformIds(createdIds);
      setSelectedPlatformId(createdIds[createdIds.length - 1] ?? null);
      triggerSnackbar(
        createdIds.length > 1 ? `${createdIds.length} platforms duplicated.` : `${platformsToCopy[0].name}-Copy added.`,
        "success"
      );
      invalidatePlatforms();
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : "Unable to duplicate platform.";
      triggerSnackbar(message, "error");
    } finally {
      setIsDuplicating(false);
    }
  }, [authorizedFetch, canManagePlatforms, getSelectedPlatformIds, invalidatePlatforms, platforms, triggerSnackbar]);

  const handleToolbarEdit = () => {
    if (!canManagePlatforms || !selectedPlatformId) {
      return;
    }
    const platform = platforms.find((entry) => entry.id === selectedPlatformId);
    if (platform) {
      handleOpenEdit(platform);
    }
  };

  const handleToolbarDelete = () => {
    if (!canManagePlatforms) {
      return;
    }
    const ids = getSelectedPlatformIds();
    if (ids.length === 0) {
      return;
    }
    const idsSet = new Set(ids);
    const selected = platforms.filter((entry) => idsSet.has(entry.id));
    if (selected.length === 0) {
      return;
    }
    handleOpenDelete(selected[0], selected.slice(1));
  };

  const handleImportFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!canManagePlatforms) {
        return;
      }
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) {
        return;
      }
      setIsImporting(true);
      try {
        const formData = new FormData();
        formData.append("file", file, file.name);

        const response = await authorizedFetch("/api/platforms/import", {
          method: "POST",
          body: formData
        });
        const payload = (await response.json().catch(() => null)) as {
          importedPlatforms?: number;
          importedStreams?: number;
          platformErrors?: unknown;
          streamErrors?: unknown;
          error?: string;
        } | null;
        if (!response.ok) {
          const message = payload?.error ?? "Failed to import configuration.";
          throw new Error(message);
        }

        const importedPlatforms = Number(payload?.importedPlatforms ?? 0);
        const importedStreams = Number(payload?.importedStreams ?? 0);
        const platformErrors =
          Array.isArray(payload?.platformErrors) && payload?.platformErrors?.every((entry) => typeof entry === "string")
            ? (payload.platformErrors as string[])
            : [];
        const streamErrors =
          Array.isArray(payload?.streamErrors) && payload?.streamErrors?.every((entry) => typeof entry === "string")
            ? (payload.streamErrors as string[])
            : [];
        const hasErrors = platformErrors.length > 0 || streamErrors.length > 0;
        const messageParts = [`Platforms: ${importedPlatforms}`, `Streams: ${importedStreams}`];
        if (hasErrors) {
          messageParts.push("Some items were skipped");
          console.warn("Import warnings", { platformErrors, streamErrors });
        }
        triggerSnackbar(`Import complete. ${messageParts.join(". ")}.`, hasErrors ? "error" : "success");
        setSelectedPlatformId(null);
        setSelectedPlatformIds([]);
        await Promise.all([invalidatePlatforms(), invalidateStreams()]);
      } catch (err) {
        console.error("Import failed", err);
        const message = err instanceof Error ? err.message : "Failed to import configuration.";
        triggerSnackbar(message, "error");
      } finally {
        setIsImporting(false);
      }
    },
    [authorizedFetch, canManagePlatforms, invalidatePlatforms, invalidateStreams, platforms, streams, triggerSnackbar]
  );

  const handleImportClick = useCallback(() => {
    if (!canManagePlatforms) return;
    fileInputRef.current?.click();
  }, [canManagePlatforms]);

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box>
          <Box display="inline-flex" alignItems="center" gap={1}>
            <ConnectingAirportsIcon sx={{ color: "text.secondary" }} />
            <Typography variant="h5" sx={{ m: 0 }}>
              Platforms
            </Typography>
            <Badge badgeContent={platforms.length} color="primary" sx={{ ml: 1 }} />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: { xs: "none", lg: "block" } }}
          >
            Catalog platforms that host your sensors or content sources.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={platformsViewMode}
            onChange={handleViewModeChange}
            aria-label="Platforms view style"
            color="primary"
            sx={{ ml: { xs: 0, sm: 0.75 } }}
          >
            <ToggleButton value="Cards" aria-label="Cards view">
              <Tooltip title="Cards" describeChild>
                <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <DashboardCustomizeIcon fontSize="small" />
                </Box>
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="Table" aria-label="Table view">
              <Tooltip title="Table" describeChild>
                <Box component="span" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <TableRowsIcon fontSize="small" />
                </Box>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          {canManagePlatforms ? (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                ml: { xs: 0, sm: 0.75 },
                flexWrap: "nowrap",
                gap: 0.5,
                overflowX: { xs: "auto", sm: "visible" },
                justifyContent: { xs: "flex-start", md: "flex-end" },
                py: { xs: 0.25, sm: 0 }
              }}
            >
              {isNarrowToolbar ? (
                <Tooltip title="Add platform">
                  <span>
                    <IconButton color="primary" aria-label="Add platform" onClick={handleOpenCreate} size="small">
                      <AddBoxIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleOpenCreate}
                  fullWidth={false}
                  size="small"
                  sx={{
                    whiteSpace: "nowrap",
                    px: 3,
                    minWidth: "auto",
                    flexShrink: 0
                  }}
                >
                  Add platform
                </Button>
              )}
              <Tooltip title="Duplicate platform">
                <span>
                  <IconButton
                    color="primary"
                    aria-label="Duplicate platform"
                    onClick={handleDuplicate}
                    disabled={!canManagePlatforms || !hasAnySelection || isDuplicating}
                    size="small"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Edit platform(s)">
                <span>
                  <IconButton
                    color="primary"
                    aria-label="Edit platform"
                    onClick={handleToolbarEdit}
                    disabled={!canManagePlatforms || !hasSingleSelection}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete platform(s)">
                <span>
                  <IconButton
                    color="primary"
                    aria-label="Delete platform"
                    onClick={handleToolbarDelete}
                    disabled={!canManagePlatforms || !hasAnySelection}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Export configuration">
                <IconButton
                  color="primary"
                  aria-label="Export configuration"
                  onClick={handleExport}
                  disabled={!canManagePlatforms || !hasAnySelection || isExporting || isLoading || isStreamsLoading}
                >
                  <FileDownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Import configuration">
                <IconButton
                  color="primary"
                  aria-label="Import configuration"
                  onClick={handleImportClick}
                  disabled={!canManagePlatforms || isImporting}
                >
                  <FileUploadIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null}
        </Stack>
      </Box>

      {isImporting ? (
        <Box sx={{ width: "100%", mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            Importing configuration...
          </Typography>
        </Box>
      ) : null}

      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : errorMessage ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : platforms.length === 0 ? (
        <Alert severity="info">
          {canManagePlatforms
            ? "No platforms configured yet. Add your first platform to get started."
            : "No platforms are configured yet. Contact an administrator to add one."}
        </Alert>
      ) : platformsViewMode === "Cards" ? (
        <PlatformCards
          platforms={platforms}
          streamsByPlatform={streamsByPlatform}
          isStreamsLoading={isStreamsLoading}
          streamsErrorMessage={streamsErrorMessage}
          getStreamState={getStreamState}
          canManagePlatforms={canManagePlatforms}
          onEdit={handleOpenEdit}
          onDelete={handleOpenDelete}
          fallbackPlatformColor={FALLBACK_PLATFORM_COLOR}
        />
      ) : (
        <PlatformTable
          platforms={platforms}
          streamsByPlatform={streamsByPlatform}
          isStreamsLoading={isStreamsLoading}
          streamsErrorMessage={streamsErrorMessage}
          getStreamState={getStreamState}
          fallbackPlatformColor={FALLBACK_PLATFORM_COLOR}
          selectedPlatformId={selectedPlatformId}
          selectedPlatformIds={selectedPlatformIds}
          onSelectionChange={handleSelectionChange}
        />
      )}

      {canManagePlatforms ? (
        <>
          <PlatformDialog
            open={dialogOpen}
            editingPlatform={editingPlatform}
            platforms={platforms}
            authorizedFetch={authorizedFetch}
            onClose={handleCloseDialog}
            onSuccess={handleDialogSuccess}
          />

          <Dialog open={Boolean(activeDeletePlatform)} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
            <DialogTitle>Delete platform{deleteTargets.length > 1 ? "s" : ""}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Typography>
                  Are you sure you want to delete{" "}
                  {deleteTargets.length > 1 ? "these platforms" : <strong>{activeDeletePlatform?.name}</strong>}?
                </Typography>
                {deleteTargets.length > 1 ? (
                  <Box component="ul" sx={{ pl: 3, mb: 0, mt: -1 }}>
                    {deleteTargets.map((platform) => (
                      <li key={platform.id}>
                        <Typography variant="body2">{platform.name}</Typography>
                      </li>
                    ))}
                  </Box>
                ) : null}
                {shouldShowStreamOptions ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Choose how to handle streams currently linked to this platform.
                    </Typography>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Streams</FormLabel>
                      <RadioGroup
                        name="delete-streams-action"
                        value={deleteAction}
                        onChange={(event) => setDeleteAction(event.target.value as DeleteStreamsAction)}
                      >
                        <FormControlLabel
                          value="detach"
                          control={<Radio />}
                          label="Keep streams and remove the platform assignment"
                        />
                        <FormControlLabel
                          value="delete"
                          control={<Radio />}
                          label="Delete all streams assigned to this platform"
                        />
                      </RadioGroup>
                    </FormControl>
                  </>
                ) : showNoStreamMessage ? (
                  <Typography variant="body2" color="text.secondary">
                    This platform does not have any streams assigned.
                  </Typography>
                ) : isStreamsLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading stream assignments...
                  </Typography>
                ) : streamsErrorMessage ? (
                  <Typography variant="body2" color="error">
                    {streamsErrorMessage}
                  </Typography>
                ) : null}
                {bulkDeleteProgress && deleteAction === "delete" ? (
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      Deleting {bulkDeleteProgress.completed} of {bulkDeleteProgress.total}
                      {bulkDeleteProgress.failed > 0 ? ` (${bulkDeleteProgress.failed} failed)` : ""}.
                    </Typography>
                    <LinearProgress variant="determinate" value={bulkDeleteProgressPercent} />
                  </Stack>
                ) : null}
                {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDelete} disabled={deleteMutation.isPending || isBulkDeleteRunning}>
                Cancel
              </Button>
              <Button onClick={handleConfirmDelete} color="error" disabled={deleteMutation.isPending || isBulkDeleteRunning}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}

      <Snackbar
        open={Boolean(snackbar?.open)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar?.severity ?? "success"}>{snackbar?.message ?? ""}</Alert>
      </Snackbar>
      <input
        type="file"
        accept="application/json"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleImportFileChange}
      />
    </Box>
  );
};

export default PlatformsPage;
